// src/components/PortalAluno/CarreiraProfissional.tsx
// UPDATED: 2026-03-27
// ✅ Dados reais do backend via useStudentProfile
// ✅ Sem botão "Adicionar"
// ✅ Mostra curso, diploma e escola do aluno
// ✅ Ícones PNG ao invés de componentes Lucide

import React, { useMemo, useState } from 'react';
import {
  Filter, Calendar, MapPin, TrendingUp, Loader2, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { useStudentProfile } from '../../hooks/useStudentProfile';

interface AtividadeCarreira {
  id: string;
  tipo: 'certificacao' | 'curso' | 'experiencia' | 'conquista';
  titulo: string;
  descricao: string;
  data: string;
  instituicao?: string;
}

type TipoFiltro = AtividadeCarreira['tipo'] | null;

const tipoConfig: Record<AtividadeCarreira['tipo'], {
  iconPath: string;
  dot: string; bg: string;
  badgeBg: string; badgeText: string; badgeBorder: string;
  statSubtitle: string;
}> = {
  certificacao: {
    iconPath: '/public/icons/icon13.png',
    dot: '#d4af37', bg: 'rgba(212,175,55,0.12)',
    badgeBg: 'bg-amber-50', badgeText: 'text-amber-700', badgeBorder: 'border-amber-200',
    statSubtitle: 'Certificados obtidos',
  },
  curso: {
    iconPath: '/public/icons/b9.png',
    dot: '#3b82f6', bg: 'rgba(59,130,246,0.10)',
    badgeBg: 'bg-blue-50', badgeText: 'text-blue-700', badgeBorder: 'border-blue-200',
    statSubtitle: 'Cursos concluídos',
  },
  experiencia: {
    iconPath: '/public/icons/carreira.png',
    dot: '#22c55e', bg: 'rgba(34,197,94,0.10)',
    badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700', badgeBorder: 'border-emerald-200',
    statSubtitle: 'Registradas',
  },
  conquista: {
    iconPath: '/public/icons/carteirinha.png',
    dot: '#a855f7', bg: 'rgba(168,85,247,0.10)',
    badgeBg: 'bg-violet-50', badgeText: 'text-violet-700', badgeBorder: 'border-violet-200',
    statSubtitle: 'Prêmios e reconhecimentos',
  },
};

const filtros: { id: TipoFiltro; label: string }[] = [
  { id: null,           label: 'Todos'         },
  { id: 'certificacao', label: 'Certificações' },
  { id: 'curso',        label: 'Cursos'        },
  { id: 'experiencia',  label: 'Experiência'   },
  { id: 'conquista',    label: 'Conquistas'    },
];

const CarreiraProfissional: React.FC = () => {
  const { student, loading, error, refetch } = useStudentProfile();
  const [filtroTipo, setFiltroTipo] = useState<TipoFiltro>(null);

  // Monta atividades reais a partir dos dados do aluno
  const atividades: AtividadeCarreira[] = useMemo(() => {
    if (!student) return [];
    const list: AtividadeCarreira[] = [];

    // Curso matriculado
    if (student.courseName) {
      list.push({
        id:          'curso-principal',
        tipo:        'curso',
        titulo:      student.courseName,
        descricao:   `Carga horária: ${student.courseWorkload ? `${student.courseWorkload}h` : 'Não informada'}`,
        data:        student.courseStartDate
                       ? String(student.courseStartDate)
                       : String(student.enrollmentDate),
        instituicao: student.affiliate?.schoolName ?? 'Federação dos Profissionais da Beleza',
      });
    }

    // Diplomas aprovados → Certificações
    const diplomasAprovados = student.diplomaRequests?.filter(r => r.status === 'approved') ?? [];
    diplomasAprovados.forEach((req, i) => {
      list.push({
        id:          `cert-${req.id}`,
        tipo:        'certificacao',
        titulo:      student.courseName ? `Diploma — ${student.courseName}` : 'Diploma Profissional',
        descricao:   `Diploma aprovado pela Federação dos Profissionais da Beleza`,
        data:        req.resolvedAt ?? req.requestedAt,
        instituicao: student.affiliate?.schoolName ?? 'FEPCSSP',
      });
    });

    // Diplomas em análise → conquista pendente
    const diplomasPendentes = student.diplomaRequests?.filter(r => r.status === 'pending') ?? [];
    diplomasPendentes.forEach(req => {
      list.push({
        id:          `pend-${req.id}`,
        tipo:        'conquista',
        titulo:      'Diploma em Análise',
        descricao:   'Solicitação de diploma enviada e aguardando aprovação da Federação',
        data:        req.requestedAt,
        instituicao: 'FEPCSSP',
      });
    });

    return list.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
  }, [student]);

  const stats = useMemo(() => {
    const c = (t: AtividadeCarreira['tipo']) => atividades.filter(a => a.tipo === t).length;
    return {
      certificacao: c('certificacao'),
      curso:        c('curso'),
      experiencia:  c('experiencia'),
      conquista:    c('conquista'),
    };
  }, [atividades]);

  const atividadesFiltradas = useMemo(() =>
    filtroTipo ? atividades.filter(a => a.tipo === filtroTipo) : atividades,
    [atividades, filtroTipo]
  );

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 text-amber-500 animate-spin"/>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <AlertTriangle className="w-10 h-10 text-amber-400"/>
      <p className="text-sm text-gray-600">{error}</p>
      <button onClick={refetch}
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
        <RefreshCw className="w-4 h-4"/> Tentar novamente
      </button>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100
        shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"/>
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="relative flex-shrink-0">
              <div className="absolute -inset-1 bg-amber-400/20 blur-[10px] rounded-full"/>
              <img src="/icons/icon-carreira.png" alt="Carreira"
                className="relative w-11 h-11 object-contain
                  drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]"
                onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}/>
            </div>
            <div>
              <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">
                Carreira Profissional
              </h1>
              <p className="text-xs text-gray-400 mt-1">Sua trajetória em um só lugar</p>
            </div>
          </div>
          <button onClick={refetch}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <RefreshCw className="w-4 h-4"/>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100 border-t border-gray-100">
          {(Object.keys(tipoConfig) as AtividadeCarreira['tipo'][]).map(tipo => {
            const cfg = tipoConfig[tipo];
            return (
              <div key={tipo}
                className="flex flex-col items-center justify-center gap-2 py-5 px-4
                  hover:bg-gray-50/70 transition-colors cursor-default">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: cfg.bg }}>
                  <img 
                    src={cfg.iconPath} 
                    alt={tipo}
                    className="w-5 h-5 object-contain"
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}
                  />
                </div>
                <div className="text-center">
                  <p className="text-2xl font-black text-gray-900 leading-none">{stats[tipo]}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5 capitalize">{tipo === 'certificacao' ? 'Certificação' : tipo === 'experiencia' ? 'Experiência' : tipo.charAt(0).toUpperCase() + tipo.slice(1)}</p>
                  <p className="text-[9px] text-gray-300 mt-0.5">{cfg.statSubtitle}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── FILTROS ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"/>
        <p className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider mr-1">
          Filtrar
        </p>
        {filtros.map(f => {
          const count = f.id
            ? atividades.filter(a => a.tipo === f.id).length
            : atividades.length;
          return (
            <button key={String(f.id)} onClick={() => setFiltroTipo(f.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                filtroTipo === f.id
                  ? 'text-[#1a1a1c] border-transparent shadow-sm'
                  : 'text-gray-500 border-gray-200 bg-white hover:border-gray-300'
              }`}
              style={filtroTipo === f.id
                ? { background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }
                : {}}>
              {f.label}
              {count > 0 && (
                <span className={`ml-1 text-[9px] font-bold px-1 py-0.5 rounded
                  ${filtroTipo === f.id ? 'bg-black/10 text-[#1a1a1c]' : 'bg-gray-100 text-gray-500'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── LISTA DE ATIVIDADES ────────────────────────────────────── */}
      {atividadesFiltradas.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 text-center">
          <TrendingUp className="w-12 h-12 text-gray-200 mx-auto mb-3"/>
          <p className="text-sm font-medium text-gray-500">Nenhuma atividade encontrada</p>
          <p className="text-xs text-gray-400 mt-1">
            {atividades.length === 0
              ? 'Suas atividades aparecerão aqui conforme você avança na formação.'
              : 'Tente outro filtro.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100
          shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-50 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-400"/>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
              Atividades · {atividadesFiltradas.length} registro{atividadesFiltradas.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="divide-y divide-gray-50">
            {atividadesFiltradas.map(atividade => {
              const cfg = tipoConfig[atividade.tipo];
              return (
                <div key={atividade.id}
                  className="flex items-start gap-4 px-5 py-4.5 py-4
                    hover:bg-gray-50/60 transition-colors">
                  {/* Ícone */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: cfg.bg }}>
                    <img 
                      src={cfg.iconPath} 
                      alt={atividade.tipo}
                      className="w-5 h-5 object-contain"
                      onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}
                    />
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <p className="text-sm font-semibold text-gray-900 leading-tight">
                        {atividade.titulo}
                      </p>
                      <span className={`inline-block px-2.5 py-0.5 rounded-lg text-[10.5px] font-semibold
                        border flex-shrink-0 ${cfg.badgeBg} ${cfg.badgeText} ${cfg.badgeBorder}`}>
                        {atividade.tipo === 'certificacao' ? 'Certificação'
                          : atividade.tipo === 'experiencia' ? 'Experiência'
                          : atividade.tipo === 'conquista' ? 'Conquista'
                          : 'Curso'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2 leading-relaxed">{atividade.descricao}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      {atividade.instituicao && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3"/> {atividade.instituicao}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3"/>
                        {new Date(atividade.data).toLocaleDateString('pt-BR',
                          { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarreiraProfissional;